import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectLogInUsername } from '../../common/authenticateDashboard/authSlice';
import { useNavigate } from "react-router-dom";
import { TiArrowBack } from 'react-icons/ti';

// Updated analytics function to work with new schema
async function getCompleteAnalytics(clientName, testName, startDate, endDate) {
    try {
        // Get metadata first
        const metaResponse = await fetch(`https://causalfunnel-21-1test.firebaseio.com/${clientName}/test/${testName}/meta_data.json`);
        const metaData = await metaResponse.json();

        // Get event data
        const dataResponse = await fetch(`https://causalfunnel-21-1test.firebaseio.com/${clientName}/test/${testName}.json`);
        const rawData = await dataResponse.json();

        if (!rawData) {
            return { error: "No data found", summary: null, detailedBreakdown: null, metaData: null };
        }

        // Process raw data into grouped format
        const processedData = {};
        processedData[testName] = {};

        Object.entries(rawData).forEach(([eventId, eventData]) => {
            const { date, event, group, time } = eventData;

            if (!processedData[testName][date]) {
                processedData[testName][date] = {
                    "A": { "views": [], "clicks": [] },
                    "B": { "views": [], "clicks": [] }
                };
            }

            if (event === 'view') {
                processedData[testName][date][group]["views"].push({
                    eventId,
                    timestamp: time,
                    time: new Date(time * 1000).toISOString()
                });
            } else if (event === 'click') {
                processedData[testName][date][group]["clicks"].push({
                    eventId,
                    timestamp: time,
                    time: new Date(time * 1000).toISOString()
                });
            }
        });

        // Sort arrays by timestamp
        Object.keys(processedData[testName]).forEach(date => {
            ['A', 'B'].forEach(group => {
                processedData[testName][date][group]["views"].sort((a, b) => a.timestamp - b.timestamp);
                processedData[testName][date][group]["clicks"].sort((a, b) => a.timestamp - b.timestamp);
            });
        });

        // Filter by date range if provided
        const filteredData = {};
        filteredData[testName] = {};
        Object.entries(processedData[testName]).forEach(([date, dayData]) => {
            const dateObj = new Date(date);
            const start = startDate ? new Date(startDate) : new Date('1970-01-01');
            const end = endDate ? new Date(endDate) : new Date('2099-12-31');

            if (dateObj >= start && dateObj <= end) {
                filteredData[testName][date] = dayData;
            }
        });

        // Generate complete summary with detailed breakdown
        const summary = {
            totalDays: 0,
            groupA: { views: 0, clicks: 0, conversionRate: 0 },
            groupB: { views: 0, clicks: 0, conversionRate: 0 },
            overallConversionRate: 0
        };

        const detailedBreakdown = {};

        Object.entries(filteredData[testName]).forEach(([date, dayData]) => {
            summary.totalDays++;

            const aViews = dayData.A.views.length;
            const aClicks = dayData.A.clicks.length;
            const bViews = dayData.B.views.length;
            const bClicks = dayData.B.clicks.length;

            summary.groupA.views += aViews;
            summary.groupA.clicks += aClicks;
            summary.groupB.views += bViews;
            summary.groupB.clicks += bClicks;

            detailedBreakdown[date] = {
                A: {
                    views: aViews,
                    clicks: aClicks,
                    conversionRate: aViews > 0 ? parseFloat((aClicks / aViews * 100).toFixed(2)) : 0
                },
                B: {
                    views: bViews,
                    clicks: bClicks,
                    conversionRate: bViews > 0 ? parseFloat((bClicks / bViews * 100).toFixed(2)) : 0
                }
            };
        });

        // Calculate final conversion rates
        summary.groupA.conversionRate = summary.groupA.views > 0 ?
            parseFloat((summary.groupA.clicks / summary.groupA.views * 100).toFixed(2)) : 0;
        summary.groupB.conversionRate = summary.groupB.views > 0 ?
            parseFloat((summary.groupB.clicks / summary.groupB.views * 100).toFixed(2)) : 0;

        const totalViews = summary.groupA.views + summary.groupB.views;
        const totalClicks = summary.groupA.clicks + summary.groupB.clicks;
        summary.overallConversionRate = totalViews > 0 ?
            parseFloat((totalClicks / totalViews * 100).toFixed(2)) : 0;

        return {
            summary,
            detailedBreakdown,
            rawProcessedData: filteredData,
            metaData
        };

    } catch (error) {
        console.error('Error fetching Firebase data:', error);
        return {
            error: error.message,
            summary: null,
            detailedBreakdown: null,
            metaData: null,
            note: "This error likely occurs due to CORS restrictions when running in browser. Try running in Node.js environment."
        };
    }
}

// Function to get list of available tests for a client
async function getAvailableTests(clientName) {
    try {
        const response = await fetch(`https://causalfunnel-21-1test.firebaseio.com/${clientName}/test.json?shallow=true`);
        const data = await response.json();
        return Object.keys(data || {});
    } catch (error) {
        console.error('Error fetching tests:', error);
        return [];
    }
}

const ABTest = () => {
    const sessionUser = useSelector(selectLogInUsername);
    const user = sessionUser?.split("@")[0];
    const clientName = user?.split("-prod")[0]; // Extract client name from user
    const navigate = useNavigate();

    // State for analytics data
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });

    // State for test selection
    const [availableTests, setAvailableTests] = useState([]);
    const [selectedTest, setSelectedTest] = useState('');
    const [loadingTests, setLoadingTests] = useState(true);
    const [status, setStatus] = useState(null);
    const [currentTestStatus, setCurrentTestStatus] = useState(null);

    useEffect(() => {
        if (!analyticsData) {
            return;
        }

        if (analyticsData?.metaData?.status) {
            setStatus(analyticsData.metaData.status);
        }
        if (analyticsData.rawProcessedData) {
            const firstTestKey = Object.keys(analyticsData.rawProcessedData)[0];
            setCurrentTestStatus(firstTestKey || null);
        }
        console.log("Current status:", analyticsData?.metaData?.status, Object.keys(analyticsData.rawProcessedData)[0]);
    }, [analyticsData]);

    // Load available tests and automatically select first test
    useEffect(() => {
        if (clientName) {
            const fetchTests = async () => {
                setLoadingTests(true);
                try {
                    const tests = await getAvailableTests(clientName);
                    setAvailableTests(tests);

                    // Auto-select first test and fetch its data
                    if (tests.length > 0) {
                        setSelectedTest(tests[0]);
                        // Fetch analytics for the first test
                        const result = await getCompleteAnalytics(clientName, tests[0], null, null);
                        if (result.error) {
                            setError(result.error);
                        } else {
                            setAnalyticsData(result);
                        }
                    }
                } catch (err) {
                    setError('Failed to load tests');
                } finally {
                    setLoadingTests(false);
                }
            };
            fetchTests();
        }
    }, [clientName]);

    // Fetch analytics data
    const fetchAnalytics = async (testName = selectedTest) => {
        if (!testName) {
            setError('Please select a test');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const startDate = dateRange.startDate || null;
            const endDate = dateRange.endDate || null;
            const result = await getCompleteAnalytics(clientName, testName, startDate, endDate);
            if (result.error) {
                setError(result.error);
            } else {
                setAnalyticsData(result);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle test selection change
    const handleTestChange = (testName) => {
        setSelectedTest(testName);
        fetchAnalytics(testName);
    };

    // Handle date range change
    const handleDateRangeChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Refresh data with new date range
    const handleRefresh = () => {
        fetchAnalytics();
    };

    const toggleStatus = async () => {
        const newStatus = status === "active" ? "paused" : "active";
        try {
            await fetch(
                `https://causalfunnel-21-1test.firebaseio.com/${clientName}/test/${currentTestStatus}/meta_data.json`,
                {
                    method: "PATCH", // PATCH updates only specified fields
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ status: newStatus }),
                }
            );
            // Update local state after successful update
            setStatus(newStatus);
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };

    return (
        <div className="flex-grow p-6" >
            {/* Back Button */}
            <div className="mb-6">
                <button
                    className="bg-indigo-500 text-white px-4 py-2 rounded-lg border 
                    border-indigo-500 hover:bg-indigo-600 transition-all duration-300 
                    ease-in-out font-semibold text-lg flex items-center gap-2"
                    onClick={() => navigate("/goals")}
                >
                    <TiArrowBack className="text-xl" />
                    Go Back to View Report Selection
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-lg">
                {/* Enhanced Header with Test Selection */}
                <div className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 rounded-t-lg">
                    <div className="px-6 py-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            {/* Left side - Title and description */}
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-3 h-3 bg-gray-600 rounded-full animate-pulse"></div>
                                    <h1 className="text-2xl font-bold">A/B Test Analytics Report</h1>
                                </div>
                                <div className="text-gray-600">
                                    {analyticsData?.metaData ? (
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                            <span className="font-medium">{analyticsData.metaData.title}</span>
                                            <span className="hidden sm:inline">•</span>
                                            <span className="bg-gray-300 text-gray-700 px-2 py-1 rounded text-xs font-medium">
                                                {analyticsData.metaData.type}
                                            </span>
                                        </div>
                                    ) : (
                                        <span>Analytics Dashboard for {clientName || 'Client'}</span>
                                    )}
                                </div>
                            </div>

                            {/* Right side - Test Selection */}
                            <div className="flex-shrink-0 w-full lg:w-1/3">
                                <div className="bg-white bg-opacity-90 border border-gray-300 rounded-lg p-4 min-w-3xl">
                                    <label className="block text-gray-700 text-sm font-medium mb-2">
                                        Select Test:
                                    </label>
                                    <select
                                        value={selectedTest}
                                        onChange={(e) => handleTestChange(e.target.value)}
                                        className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        disabled={loadingTests}
                                    >
                                        <option value="">Choose a test to analyze</option>
                                        {availableTests.map(test => (
                                            <option key={test} value={test}>{test}</option>
                                        ))}
                                    </select>
                                    {loadingTests && (
                                        <div className="text-gray-600 text-xs mt-1">Loading tests...</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Test URL if available */}
                        {analyticsData?.metaData?.url && (
                            <div className="mt-4 pt-4 border-t border-gray-300">
                                <div className="flex items-center gap-2 text-gray-600">
                                    <span className="text-sm">Test URL:</span>
                                    <a
                                        href={analyticsData.metaData.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-gray-800 hover:text-gray-900 underline transition-colors duration-200 text-sm"
                                    >
                                        {analyticsData.metaData.url}
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Date Range Controls */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">Start Date:</label>
                            <input
                                type="date"
                                value={dateRange.startDate}
                                onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="All dates"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">End Date:</label>
                            <input
                                type="date"
                                value={dateRange.endDate}
                                onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="All dates"
                            />
                        </div>
                        <button
                            onClick={handleRefresh}
                            disabled={loading || !selectedTest}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium transition-colors duration-200"
                        >
                            {loading ? 'Loading...' : 'Analyze Data'}
                        </button>

                        {
                            status && (
                                <button
                                    onClick={toggleStatus}
                                    className={`px-4 py-2 rounded-md text-white text-sm font-medium transition-colors duration-200 flex items-center gap-2 ${status === "active"
                                        ? "bg-amber-500 hover:bg-amber-600"
                                        : "bg-green-600 hover:bg-green-700"
                                        }`}
                                >

                                    {status === "active" ? "Pause" : "Resume"}
                                </button>
                            )
                        }


                        <div className="text-xs text-gray-500">
                            Leave dates empty to analyze all available data
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loadingTests && (
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                            <p className="mt-2 text-gray-600">Loading available tests...</p>
                        </div>
                    )}

                    {loading && (
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                            <p className="mt-2 text-gray-600">Loading analytics data...</p>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                            <div className="text-red-800 font-medium">Error loading data:</div>
                            <div className="text-red-700 text-sm mt-1">{error}</div>
                        </div>
                    )}

                    {!clientName && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                            <div className="text-yellow-800 font-medium">Authentication Required</div>
                            <div className="text-yellow-700 text-sm mt-1">Please log in to access your analytics data.</div>
                        </div>
                    )}

                    {analyticsData && !loading && !error && (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <h3 className="text-lg font-semibold text-blue-800 mb-2">Group A Performance</h3>
                                    {analyticsData.metaData?.preview_links?.control && (
                                        <a
                                            href={analyticsData.metaData.preview_links.control}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-2 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors duration-200 no-underline"
                                        >
                                            Preview
                                        </a>
                                    )}
                                    <div className="mt-2 space-y-1 text-sm">
                                        <p><span className="font-medium">Views:</span> {analyticsData.summary.groupA.views}</p>
                                        <p><span className="font-medium">Clicks:</span> {analyticsData.summary.groupA.clicks}</p>
                                        <p><span className="font-medium">Conversion:</span> {analyticsData.summary.groupA.conversionRate}%</p>
                                    </div>
                                </div>

                                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                    <h3 className="text-lg font-semibold text-green-800 mb-2">Group B Performance</h3>
                                    {analyticsData.metaData?.preview_links?.variant && (
                                        <a
                                            href={analyticsData.metaData.preview_links.variant}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-2 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors duration-200 no-underline"
                                        >
                                            Preview
                                        </a>
                                    )}
                                    <div className="space-y-1 text-sm mt-2">
                                        <p><span className="font-medium">Views:</span> {analyticsData.summary.groupB.views}</p>
                                        <p><span className="font-medium">Clicks:</span> {analyticsData.summary.groupB.clicks}</p>
                                        <p><span className="font-medium">Conversion:</span> {analyticsData.summary.groupB.conversionRate}%</p>
                                    </div>
                                </div>

                                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                    <h3 className="text-lg font-semibold text-purple-800 mb-2">Overall Summary</h3>
                                    <div className="space-y-1 text-sm">
                                        <p><span className="font-medium">Total Days:</span> {analyticsData.summary.totalDays}</p>
                                        <p><span className="font-medium">Overall Rate:</span> {analyticsData.summary.overallConversionRate}%</p>
                                        <p><span className="font-medium">Winner:</span>
                                            <span className={`ml-1 font-bold ${analyticsData.summary.groupB.conversionRate > analyticsData.summary.groupA.conversionRate
                                                ? 'text-green-600' : 'text-blue-600'
                                                }`}>
                                                Group {analyticsData.summary.groupB.conversionRate > analyticsData.summary.groupA.conversionRate ? 'B' : 'A'}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Detailed Daily Breakdown Table */}
                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                    <h3 className="text-lg font-semibold text-gray-800">Daily Breakdown</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Group A Views</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Group A Clicks</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Group A Rate</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Group B Views</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Group B Clicks</th>
                                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Group B Rate</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {Object.entries(analyticsData.detailedBreakdown).map(([date, data]) => (
                                                <tr key={date} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{date}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{data.A.views}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{data.A.clicks}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                        <span className="text-blue-600 font-medium">{data.A.conversionRate}%</span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{data.B.views}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">{data.B.clicks}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                        <span className="text-green-600 font-medium">{data.B.conversionRate}%</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}

                    {!analyticsData && !loading && !error && !loadingTests && availableTests.length === 0 && (
                        <div className="text-center py-8">
                            <p className="text-gray-600">No tests available for this client.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ABTest;


